// God-mode dashboard. Surfaces the operations a master account would
// run through during an elevated session: pending approvals, runner
// health, recent god-tier activity. Only useful while elevated; if a
// non-god user lands here we redirect them home.
//
// Why a dedicated screen vs. just relying on /admin/users + /admin/runners:
// During god mode you typically want a one-glance view of "what needs
// my attention right now," not to navigate between three admin pages.
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Lock,
  Loader2,
  Check,
  X,
  Server,
  ScrollText,
  Users,
  Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import {
  approveUser,
  listAllRunners,
  listHistory,
  listPendingUsers,
  listProjects,
  rejectUser,
  revokeGod,
  type AuditRow,
  type PendingUser,
  type ProjectSummary,
  type RunnerSummary,
} from "@/lib/api";

const HEARTBEAT_FRESH_MS = 60_000;

export function GodDashboardPage() {
  const { me, refresh } = useAuth();
  const [pending, setPending] = useState<PendingUser[] | null>(null);
  const [runners, setRunners] = useState<RunnerSummary[] | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [now, setNow] = useState(() => Date.now());

  async function refreshAll() {
    const [p, r, a, pr] = await Promise.all([
      listPendingUsers(),
      listAllRunners(),
      listHistory({ limit: 25 }),
      listProjects(),
    ]);
    setPending(p);
    setRunners(r);
    setAudit(a);
    setProjects(pr);
  }

  useEffect(() => {
    void refreshAll();
    const t = setInterval(refreshAll, 15_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  // If god mode isn't active, kick the user to home. The ribbon up top
  // can lead them back here once they elevate.
  if (me && !me.god.active) {
    return <Navigate to="/" replace />;
  }

  const expiresIn =
    me && me.god.active
      ? Math.max(0, new Date(me.god.expiresAt).getTime() - now)
      : 0;

  return (
    <div className="container py-8 space-y-8">
      <header className="rounded-lg border bg-gradient-to-br from-accent/30 via-card to-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Unlock className="h-5 w-5 text-accent-foreground" />
              <h1 className="text-2xl font-semibold tracking-tight">
                God mode
              </h1>
              <Badge variant="accent" className="font-mono">
                {formatRemaining(expiresIn)} remaining
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              You're elevated. Audit-logged, time-boxed. Click revoke
              when done.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              await revokeGod();
              await refresh();
            }}
          >
            <Lock className="mr-2 h-4 w-4" />
            Revoke now
          </Button>
        </div>
      </header>

      <SectionPending pending={pending} projects={projects} onResolved={refreshAll} />
      <SectionRunners runners={runners} />
      <SectionAudit audit={audit} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pending approvals
// ─────────────────────────────────────────────────────────────────────────

function SectionPending({
  pending,
  projects,
  onResolved,
}: {
  pending: PendingUser[] | null;
  projects: ProjectSummary[];
  onResolved: () => void | Promise<void>;
}) {
  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-medium">Pending approvals</h2>
          {pending && (
            <Badge variant={pending.length > 0 ? "warning" : "outline"}>
              {pending.length}
            </Badge>
          )}
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/users">All users →</Link>
        </Button>
      </header>

      {!pending && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      {pending && pending.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No one is waiting. All caught up.
        </p>
      )}
      {pending && pending.length > 0 && (
        <div className="space-y-3">
          {pending.map((u) => (
            <PendingCard
              key={u.userId}
              user={u}
              projects={projects}
              onResolved={onResolved}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PendingCard({
  user,
  projects,
  onResolved,
}: {
  user: PendingUser;
  projects: ProjectSummary[];
  onResolved: () => void | Promise<void>;
}) {
  const defaultProject = useMemo(
    () => projects.find((p) => p.id === "jobgoblin")?.id ?? projects[0]?.id,
    [projects]
  );
  const [busy, setBusy] = useState(false);

  async function approve() {
    if (!defaultProject) return;
    setBusy(true);
    try {
      await approveUser(user.userId, {
        role: "viewer",
        projectIds: [defaultProject],
      });
      await onResolved();
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await rejectUser(user.userId);
      await onResolved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="font-medium">{user.email ?? user.userId}</div>
          <div className="text-xs text-muted-foreground">
            requested {new Date(user.requestedAt).toLocaleString()}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={reject}
            disabled={busy}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="mr-1 h-3.5 w-3.5" /> Reject
          </Button>
          <Button size="sm" onClick={approve} disabled={busy || !defaultProject}>
            <Check className="mr-1 h-3.5 w-3.5" /> Approve as viewer
          </Button>
        </div>
      </div>
      {user.message && (
        <div className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
          {user.message}
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Quick approve grants viewer on{" "}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
          {defaultProject ?? "(no projects)"}
        </code>
        . For finer control,{" "}
        <Link to="/admin/users" className="text-info underline">
          open the full users page
        </Link>
        .
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Runners
// ─────────────────────────────────────────────────────────────────────────

function SectionRunners({ runners }: { runners: RunnerSummary[] | null }) {
  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-medium">Runners</h2>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/runners">Manage →</Link>
        </Button>
      </header>

      {!runners && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      {runners && runners.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No runners registered. Spin one up with{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
            yarn dev:runner
          </code>{" "}
          on a host and it'll auto-register on first heartbeat.
        </p>
      )}
      {runners && runners.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {runners.map((r) => (
            <RunnerTile key={r.id} runner={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function RunnerTile({ runner }: { runner: RunnerSummary }) {
  const ageMs = runner.last_seen_at
    ? Date.now() - new Date(runner.last_seen_at).getTime()
    : Number.POSITIVE_INFINITY;
  const status: "online" | "stale" | "offline" =
    ageMs < HEARTBEAT_FRESH_MS
      ? "online"
      : ageMs < HEARTBEAT_FRESH_MS * 5
        ? "stale"
        : "offline";

  return (
    <div className="rounded-lg border bg-card p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium">{runner.name}</span>
        <Badge
          variant={
            status === "online"
              ? "success"
              : status === "stale"
                ? "warning"
                : "outline"
          }
        >
          {status}
        </Badge>
      </div>
      <div className="font-mono text-xs text-muted-foreground">{runner.id}</div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        <Badge
          variant={
            runner.trust_level === "self"
              ? "default"
              : runner.trust_level === "trusted"
                ? "info"
                : "destructive"
          }
        >
          {runner.trust_level}
        </Badge>
        <span className="text-muted-foreground">
          {runner.capabilities.length === 0
            ? "no caps"
            : runner.capabilities.join(", ")}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Audit
// ─────────────────────────────────────────────────────────────────────────

function SectionAudit({ audit }: { audit: AuditRow[] | null }) {
  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-medium">Recent activity</h2>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/history">Full history →</Link>
        </Button>
      </header>

      {!audit && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      {audit && audit.length === 0 && (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      )}
      {audit && audit.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Time</th>
                <th className="px-4 py-2">Decision</th>
                <th className="px-4 py-2">Task</th>
                <th className="px-4 py-2">Tier</th>
                <th className="px-4 py-2">Actor</th>
              </tr>
            </thead>
            <tbody>
              {audit.slice(0, 10).map((r) => (
                <tr
                  key={r.id}
                  className={
                    "border-b last:border-0 " +
                    (r.god_session_id ? "bg-accent/10" : "")
                  }
                >
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={
                        r.decision === "allowed" ? "success" : "destructive"
                      }
                    >
                      {r.decision}
                    </Badge>
                    {r.god_session_id && (
                      <span className="ml-1" title="god session">
                        🔓
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {r.task_id ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    {r.tier && <Badge variant="outline">{r.tier}</Badge>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.actor_email ?? r.actor_id.slice(0, 14)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatRemaining(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
